import { type FC, type ReactNode, useEffect } from 'react';
import { Box, ContentBlock, ToastProvider } from 'braid-design-system';
import { observer } from 'mobx-react';
import AutoScrollIntoViewForAndroid from '../../components/common/AutoScrollIntoViewForAndroid';
import AppErrorBoundary from '../../components/app/AppErrorBoundary';
// @ts-ignore
import connectMediaQuery from '../../components/hoc/connectMediaQuery';
import CandidateManagementPage from '../../pages/CandidateManagementPage';
import { FeatureConfigProvider } from './FeatureContext';
import { getAdvertiserIds } from 'src/utils/advertiserIds';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TrackingOrigins, linkRenderer } from 'src/components/app/TrackingLink';
import { RenderOnClient } from 'src/libs/RenderOnClient';
import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import { environments } from 'src/siteConfig';
import * as styles from './CandidateManagement.css';
import { useHeaderFooterContextQuery } from 'src/libs/HeaderFooterContext';
import { UnhandledErrorPageBlock } from 'src/components/app';
import { useStores } from 'src/utils/hooks/useStores';
import { CandidateManagementAppSkeleton } from '../AppPageBlock/CandidateManagementAppSkeleton'; // FIXME: This should come from `../CandidateManagementApp` i.e. rename AppPageBlock and use index.ts to export

const CandidateManagement: FC = () => (
  <RenderOnClient fallback={<PageSkeleton />}>
    <CandidateManagementApp />
  </RenderOnClient>
);

export default CandidateManagement;

// Note: isMobile is injected here by connectMediaQuery
const CandidateManagementBaseApp: FC<{ isMobile: boolean }> = ({
  isMobile,
}) => {
  const store = useStores();
  const { seekAdvertiserId } = getAdvertiserIds();
  const { site, locale, environment } = useAppConfig();

  // Note 1: this call and the one in `src/graphql/hirer/schema/queryHeaderFooterContext.ts` (for MobX) must satisfy these conditions:
  // - this call's selection set must be the super set of MobX query
  // - variable shape must be the same
  // Reason: this is to force the MobX query to use the cache data fetched from this query.
  // Once we removed the MobX query, we can freely change the shape of the query.
  const { data, loading, error } = useHeaderFooterContextQuery({
    fetchPolicy: 'cache-first',
    onCompleted: ({ headerFooterContext }) => {
      store.userStore.load({ accountInfo: headerFooterContext });
    },
  });

  const isStaging = environment === environments.staging;

  useEffect(() => {
    store.setMatchMedia(isMobile);
  }, [store, isMobile]);

  if (error) {
    return (
      <CandidateManagementLayout
        header={
          <EmployerHeader
            activeTabId="jobs"
            site={site}
            locale={locale}
            isStaging={isStaging}
            error={error}
          />
        }
        content={
          <ContentBlock>
            <Box margin="small">
              <UnhandledErrorPageBlock />
            </Box>
          </ContentBlock>
        }
        footer={
          <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
        }
      />
    );
  }

  if (
    loading ||
    !data ||
    data.currentAdvertiser.__typename === 'MissingAdvertiserIdClaimError'
  ) {
    return <PageSkeleton />;
  }

  const loadedEmployerHeader = (
    <div className={styles.header}>
      <EmployerHeader
        activeTabId="jobs"
        site={site}
        locale={locale}
        isStaging={isStaging}
        linkRenderer={linkRenderer(TrackingOrigins.TRACKING_ORIGIN_HEADER)}
        identityContext={data.headerFooterContext?.identityContext}
        advertiser={data.headerFooterContext?.advertiser}
        user={data.headerFooterContext?.user}
        data-testid="employer-header"
      />
    </div>
  );

  return (
    <ToastProvider>
      <FeatureConfigProvider advertiserSeekId={seekAdvertiserId} site={site}>
        <AutoScrollIntoViewForAndroid />

        <CandidateManagementLayout
          header={isMobile ? null : loadedEmployerHeader}
          content={
            <AppErrorBoundary>
              <CandidateManagementPage employerHeader={loadedEmployerHeader} />
            </AppErrorBoundary>
          }
          footer={
            <EmployerFooter
              site={site}
              locale={locale}
              isStaging={isStaging}
              identityContext={data.headerFooterContext?.identityContext}
              advertiser={data.headerFooterContext?.advertiser}
            />
          }
        />
      </FeatureConfigProvider>
    </ToastProvider>
  );
};
const CandidateManagementApp = connectMediaQuery(
  observer(CandidateManagementBaseApp),
);

/**
 * Static and Loading states
 */
const PageSkeleton = () => {
  const { site, locale, environment } = useAppConfig();

  const isStaging = environment === environments.staging;

  return (
    <CandidateManagementLayout
      header={
        <EmployerHeader
          activeTabId="jobs"
          site={site}
          locale={locale}
          isStaging={isStaging}
        />
      }
      content={<CandidateManagementAppSkeleton />}
      footer={
        <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
      }
    />
  );
};

const CandidateManagementLayout: FC<{
  header: ReactNode;
  content: ReactNode;
  footer: ReactNode;
}> = ({ header, content, footer }) => (
  <>
    {header}
    {content}
    {footer}
  </>
);
